import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import { SUPPORTED_LANGUAGES } from '../../i18n/config';
import { LANGUAGE_META } from '../../i18n/languageMeta';
import { useLanguageSelection } from '../../hooks/useLanguageSelection';

/**
 * LanguageSwitcher — dropdown that flips the FE UI language and (when
 * authed) persists the choice on the user's profile.
 *
 * Renders whenever there's more than one supported language to choose
 * from, in BOTH auth states. A single-language config
 * (`VITE_SUPPORTED_LANG=en`) renders nothing.
 *
 * Click flow (optimistic):
 *   1. `i18n.changeLanguage(code)` — UI flips immediately, dictionary
 *      switches, `Accept-Language` + `?l=` start landing on outbound calls.
 *      i18next's LanguageDetector caches the code to localStorage
 *      (`i18nextLng`), so the choice survives reloads even with no profile.
 *   2. Authed only — `PATCH /profile {language: code}`: BE persists the
 *      choice canonically; the `?l=` param sent on subsequent unauth
 *      surfaces also tracks the attempt in `metadata.lang[]`. If the PATCH
 *      fails, revert i18n back to the prior language. The apiService
 *      error-modal pipeline surfaces the failure (translated via
 *      `errors:LANGUAGE_NOT_SUPPORTED` if the BE rejected the code).
 *   3. Unauthed — no PATCH; the localStorage cache from step 1 is the
 *      only persistence. Once the user authenticates, `authService`
 *      overrides the FE language with their saved `Profile.language`.
 *
 * See `docs/i18n.md`.
 */
export const LanguageSwitcher: React.FC = () => {
  const { t } = useTranslation('common');
  const { current, select, isPending } = useLanguageSelection();

  const [open, setOpen] = useState(false);
  // Which edge of the trigger the menu anchors to. 'right' anchors the menu's
  // right edge to the trigger (builds leftward) — correct when the trigger
  // sits near the viewport's right edge (ProfileMenu, Settings). 'left' builds
  // rightward — correct when the trigger is left-justified (the location gate),
  // where a leftward menu would clip off-screen. Recomputed on each open.
  const [menuAlign, setMenuAlign] = useState<'left' | 'right'>('right');
  const rootRef = useRef<HTMLDivElement>(null);

  // Decide the anchor side before paint (useLayoutEffect → no flicker):
  // prefer building rightward, fall back to leftward only if the menu would
  // overflow the right viewport edge from the trigger's left position.
  useLayoutEffect(() => {
    if (!open || !rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const MENU_WIDTH = 176; // min-w-[10rem] (160px) + slack
    const MARGIN = 8;
    const roomRight = window.innerWidth - rect.left;
    setMenuAlign(roomRight >= MENU_WIDTH + MARGIN ? 'left' : 'right');
  }, [open]);

  // Close on outside click. Pointerdown fires before click, so a tap on
  // a menu row still selects before the close handler nukes the menu.
  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open]);

  if (SUPPORTED_LANGUAGES.length < 2) return null;

  const handleSelect = async (next: string) => {
    setOpen(false);
    await select(next);
  };

  const currentMeta = LANGUAGE_META[current];

  return (
    <div ref={rootRef} className="relative inline-block text-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={isPending}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Change language (currently ${currentMeta?.nativeName ?? current.toUpperCase()})`}
        className="inline-flex items-center gap-1.5 rounded-md border border-surface-300 bg-background text-foreground px-2 py-1 font-medium hover:bg-surface-100 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {currentMeta && (
          <img
            src={currentMeta.flag}
            alt=""
            aria-hidden="true"
            className="h-3.5 w-5 rounded-sm object-cover"
          />
        )}
        <span>{currentMeta?.nativeName ?? current.toUpperCase()}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t('ui.languageAria')}
          className={`absolute mt-1 min-w-[10rem] rounded-md border border-surface-300 bg-popover text-popover-foreground py-1 shadow-lg z-10 ${
            menuAlign === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {SUPPORTED_LANGUAGES.map((code) => {
            const meta = LANGUAGE_META[code];
            const isCurrent = code === current;
            return (
              <li key={code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isCurrent}
                  onClick={() => handleSelect(code)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-surface-100 ${
                    isCurrent ? 'font-semibold' : ''
                  }`}
                >
                  {meta && (
                    <img
                      src={meta.flag}
                      alt=""
                      aria-hidden="true"
                      className="h-3.5 w-5 rounded-sm object-cover"
                    />
                  )}
                  <span>{meta?.nativeName ?? code.toUpperCase()}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
